import {
  Combobox,
  type SelectOption,
  type SelectProps,
} from '@payaca/ambrosia';
import { Tag as TagType } from '@payaca/types/tagTypes';
import { useMemo } from 'react';
import Tag from '../tag/Tag';

type TagId = number | string;

type Props<TIdType extends TagId, TMultipleBoolean extends boolean> = Omit<
  SelectProps<
    TIdType,
    TMultipleBoolean,
    Pick<TagType, 'tagText' | 'tagColour'>
  >,
  'options' | 'listboxOptionClassName' | 'CustomOption'
> & {
  availableTags: (Pick<TagType, 'tagText' | 'tagColour'> & { id: TIdType })[];
  className?: string;
};

const TagSelectCombobox = <
  TIdType extends TagId,
  TMultipleBoolean extends boolean,
>({
  availableTags,
  value,
  multiple,
  onChange,
  CustomSelected,
  className,
  ...props
}: Props<TIdType, TMultipleBoolean>) => {
  const sortedTags: SelectOption<
    TIdType,
    Pick<TagType, 'tagText' | 'tagColour'>
  >[] = useMemo(() => {
    return availableTags
      .sort((a, b) => {
        return a.tagText.localeCompare(b.tagText);
      })
      .map((tag) => {
        return {
          label: tag.tagText,
          value: tag.id,
          metadata: tag,
        };
      });
  }, [availableTags]);

  return (
    <Combobox
      options={sortedTags}
      comboboxOptionClassName="pl-2 pt-1 pb-1"
      className={className}
      CustomSelected={
        CustomSelected
          ? CustomSelected
          : ({ selectedOptions }) => {
              return (
                <div className="flex flex-row flex-wrap gap-1">
                  {selectedOptions?.map((o) => {
                    return (
                      <span key={o.value} className="min-w-0">
                        <Tag
                          colour={o?.metadata?.tagColour}
                          isRemovable={multiple}
                          onRemoveTag={
                            multiple
                              ? () => {
                                  onChange?.(
                                    (value as TIdType[]).filter(
                                      (v) => v !== o.value
                                    ) as TMultipleBoolean extends true
                                      ? TIdType[]
                                      : TIdType,
                                    { close: () => {} }
                                  );
                                }
                              : undefined
                          }
                        >
                          {o?.metadata?.tagText || ''}
                        </Tag>
                      </span>
                    );
                  })}
                </div>
              );
            }
      }
      CustomOption={({ option }) => {
        return (
          <span className="min-w-0">
            <Tag colour={option?.metadata?.tagColour}>
              {option?.metadata?.tagText || ''}
            </Tag>
          </span>
        );
      }}
      onChange={onChange}
      value={value}
      multiple={multiple}
      dynamicOptionsWidth
      {...props}
    />
  );
};

export default TagSelectCombobox;
